import { PAGE_PERMISSION_CODES, PERMISSION_CODES } from '@core-ui/common';
import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ProtectedView } from '@moniebook-web/components/molecules/protected-view';
import { Outlet, type RouteObject } from 'react-router';

import { StyleScope } from '@/features/shared/style-scope';
import { FEATURE_FLAGS } from '@/shared/constants';

export const settingsRoutes: RouteObject[] = [
    {
        path: 'settings',
        element: <StyleScope name='settings-ui' />,
        children: [
            {
                path: 'personal',
                async lazy() {
                    const { personalInformation } = await unwrapFeatures();
                    return personalInformation().then((m) => ({
                        Component: m.default,
                    }));
                },
            },
            {
                path: 'change-password',
                async lazy() {
                    const { changePassword } = await unwrapFeatures();
                    return changePassword().then((m) => ({
                        Component: m.default,
                    }));
                },
            },
            {
                path: 'preferences',
                element: (
                    <FeatureProtectedView
                        flag={FEATURE_FLAGS.ENABLE_USER_TIMEZONE_PREFERENCE}>
                        <Outlet />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { preferences } = await unwrapFeatures();
                            return preferences().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'business',
                element: (
                    <ProtectedView
                        accessCodeList={PAGE_PERMISSION_CODES.BUSINESS}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { businessInformation } =
                                await unwrapFeatures();
                            return businessInformation().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'subscriptions',
                element: (
                    <ProtectedView
                        accessCodeList={[PERMISSION_CODES.MANAGE_SUBSCRIPTION]}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { subscriptions } = await unwrapFeatures();
                            return subscriptions().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'cancel-plan',
                element: (
                    <ProtectedView
                        accessCodeList={[PERMISSION_CODES.MANAGE_SUBSCRIPTION]}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { cancelSubscriptionPage } =
                                await unwrapFeatures();
                            return cancelSubscriptionPage().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            // -- Payment-completion pages: reached mid-flow to charge the user
            // (free-trial pay-now, cancelled/expired resubscribe, plan-change resubscribe) --
            {
                path: 'confirm-payment',
                element: (
                    <FeatureProtectedView
                        altView={
                            <ProtectedView
                                accessCodeList={[
                                    PERMISSION_CODES.MANAGE_SUBSCRIPTION,
                                ]}
                            />
                        }
                        flag={FEATURE_FLAGS.VERIFY_BRANCH_BEFORE_PAYMENT}>
                        <Outlet />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { subscriptionPaymentGuard } =
                                await unwrapFeatures();
                            return subscriptionPaymentGuard().then((m) => ({
                                Component: m.default,
                            }));
                        },
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { confirmPayment } =
                                        await unwrapFeatures();
                                    return confirmPayment().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'complete-payment',
                element: (
                    <FeatureProtectedView
                        altView={
                            <ProtectedView
                                accessCodeList={[
                                    PERMISSION_CODES.MANAGE_SUBSCRIPTION,
                                ]}
                            />
                        }
                        flag={FEATURE_FLAGS.VERIFY_BRANCH_BEFORE_PAYMENT}>
                        <Outlet />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { subscriptionPaymentGuard } =
                                await unwrapFeatures();
                            return subscriptionPaymentGuard().then((m) => ({
                                Component: m.default,
                            }));
                        },
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { completePayment } =
                                        await unwrapFeatures();
                                    return completePayment().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'confirm-plan',
                element: (
                    <FeatureProtectedView
                        altView={
                            <ProtectedView
                                accessCodeList={[
                                    PERMISSION_CODES.MANAGE_SUBSCRIPTION,
                                ]}
                            />
                        }
                        flag={FEATURE_FLAGS.VERIFY_BRANCH_BEFORE_PAYMENT}>
                        <Outlet />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { subscriptionPaymentGuard } =
                                await unwrapFeatures();
                            return subscriptionPaymentGuard().then((m) => ({
                                Component: m.default,
                            }));
                        },
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { confirmPlan } =
                                        await unwrapFeatures();
                                    return confirmPlan().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            // -- Plan-browsing pages: viewing/choosing a plan before payment --
            {
                path: 'current-plan',
                element: (
                    <ProtectedView
                        accessCodeList={[PERMISSION_CODES.MANAGE_SUBSCRIPTION]}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { currentPlan } = await unwrapFeatures();
                            return currentPlan().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'select-plan',
                element: (
                    <ProtectedView
                        accessCodeList={[PERMISSION_CODES.MANAGE_SUBSCRIPTION]}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { selectPlan } = await unwrapFeatures();
                            return selectPlan().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'change-plan',
                async lazy() {
                    const { changeSubscriptionGuard } = await unwrapFeatures();
                    return changeSubscriptionGuard().then((m) => ({
                        Component: m.default,
                    }));
                },
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { changePlan } = await unwrapFeatures();
                            return changePlan().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'checkout',
                        async lazy() {
                            const { changePlanCheckout } =
                                await unwrapFeatures();
                            return changePlanCheckout().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'billing-history',
                element: (
                    <ProtectedView
                        accessCodeList={[PERMISSION_CODES.MANAGE_SUBSCRIPTION]}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { billingHistory } = await unwrapFeatures();
                            return billingHistory().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'payment-methods',
                element: (
                    <ProtectedView
                        accessCodeList={[
                            PERMISSION_CODES.MANAGE_PAYMENT_METHOD,
                        ]}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { paymentMethods } = await unwrapFeatures();
                            return paymentMethods().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        async lazy() {
                            const { addPaymentMethod } = await unwrapFeatures();
                            return addPaymentMethod().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
        ],
    },
];

async function unwrapFeatures() {
    const features = await import('settings/features');

    return features.default;
}
